import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { HashLink } from 'react-router-hash-link';
import { injectUrlVars } from 'Utils/Validators';
import { isExternalLink, isValidTarget } from 'Utils';

class Link extends React.Component {
  static propTypes = {
    additionalProps: PropTypes.object,
    ariaLabel: PropTypes.string,
    ariaLabelledby: PropTypes.string,
    children: PropTypes.node,
    className: PropTypes.string,
    e2e: PropTypes.string,
    forwardedRef: PropTypes.object,
    isExternal: PropTypes.bool,
    label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    onClick: PropTypes.func,
    onMouseOut: PropTypes.func,
    onMouseOver: PropTypes.func,
    rel: PropTypes.string,
    screenReaderLabel: PropTypes.string,
    target: PropTypes.string,
    title: PropTypes.string,
    to: PropTypes.string,
    trackingAction: PropTypes.string,
    trackingLabel: PropTypes.string,
  };

  static defaultProps = {
    isExternal: false,
    label: '',
    to: '',
  };

  getAccessibleLabel = (props) => {
    const { ariaLabel, children, label, screenReaderLabel = '', target, to } = props;
    let accessibleLabel = '';

    if (ariaLabel) {
      return ariaLabel;
    }

    if (to && to.startsWith('tel:')) {
      // generate a phone number with three comma-separated numbers per block.
      // With this technique, a phone number isn't read as a single number. Instead, the number is separated into blocks with speaking pauses.
      let phoneNumber = to.replaceAll(' ', '').replace('tel:', '');
      const cleanedPhoneNumber = phoneNumber;
      let text = 'Telefonnummer';

      if (phoneNumber.startsWith('+49')) {
        text += ' mit Vorwahl aus Deutschland';
        phoneNumber = phoneNumber.replace('+49', '0');
      }

      text +=
        ' ' +
        phoneNumber
          .split('')
          .map((character, characterIndex) => {
            let result = character;
            const isLastIndex = characterIndex === phoneNumber.length - 1;
            if (!isLastIndex) {
              result += ' ';
            }
            if ((characterIndex + 1) % 3 === 0 && !isLastIndex) {
              result += ', ';
            }
            return result;
          })
          .join('');
      text += ' anrufen';

      return (
        (cleanedPhoneNumber === label ? '' : label + ' - ') + `${text} ${screenReaderLabel}`.trim()
      );
    }

    if (to && to.startsWith('mailto:')) {
      const cleanedEmail = to.replaceAll(' ', '').replace('mailto:', '').split('?')[0];
      const text = `E-Mail an ${cleanedEmail} senden`;

      return (cleanedEmail === label ? '' : label + ' - ') + `${text} ${screenReaderLabel}`.trim();
    }

    if (children && label && typeof label === 'string') {
      accessibleLabel = label;
    }

    if (screenReaderLabel) {
      return `${accessibleLabel} ${screenReaderLabel}`.trim();
    }

    if (target && target === '_blank') {
      return `${accessibleLabel} - öffnet in neuem Tab oder Fenster`.trim();
    }

    if (accessibleLabel !== '') {
      return accessibleLabel;
    }
  };

  render() {
    const {
      additionalProps,
      ariaLabel,
      ariaLabelledby,
      children,
      className,
      e2e,
      isExternal: isExternalParameter,
      label,
      onClick,
      onMouseOut,
      onMouseOver,
      forwardedRef,
      rel,
      target,
      title,
      to,
      trackingAction,
      trackingLabel,
    } = this.props;

    const isExternal = isExternalParameter || isExternalLink(to);

    const propDataTrackingLabel =
      !trackingLabel && label ? `${label?.props?.children ?? label}`.toLowerCase() : trackingLabel;

    let propRel = rel;
    if (!propRel && isExternal) {
      propRel = 'noopener noreferrer';
    }

    const propTo = injectUrlVars(to);
    const isTel = to.startsWith('tel:');
    const isMailto = to.startsWith('mailto:');

    let propTarget = isValidTarget(target) ? target : undefined;
    if (isExternal && !propTarget) {
      propTarget = '_blank';
    }

    const content = children ? children : label;
    const contentIsString = typeof content === 'string';
    const propAriaLabel = this.getAccessibleLabel({
      ...this.props,
      to: propTo,
      rel: propRel,
      trackingLabel: propDataTrackingLabel,
      target: propTarget,
    });

    const dataOnlyProps = Object.fromEntries(
      Object.entries(this.props).filter(([key]) => key.startsWith('data-'))
    );

    /*
      WCAG-konforme Beschriftung
      - prop `ariaLabel` ersetzt jeden anderen, möglichen String für Attribut `aria-label`
      - prop `screenReaderLabel` ist ein Zusatz zum bestehenden, zusammen gebauten String
      - Ziel ist es das Attribut `aria-label` zu befüllen als einen sr-only-Container hinzu zu fügen.

      Für Komponenten, die prop `children` nutzen, sollten den textuellen Inhalt
      über prop `label` zusätzlich an diese Komponente übergeben.
    */
    const useAriaLabelAttribute = ariaLabel || contentIsString || (!contentIsString && label);

    const basicProps = {
      'aria-label': useAriaLabelAttribute ? propAriaLabel : undefined,
      'aria-labelledby': ariaLabelledby,
      className: className,
      'data-e2e': e2e,
      'data-track-action': trackingAction,
      'data-tracking-label': propDataTrackingLabel,
      onClick: onClick,
      onMouseOut: onMouseOut,
      onMouseOver: onMouseOver,
      ref: forwardedRef,
      rel: propRel,
      target: propTarget,
      title: title,
      ...dataOnlyProps,
    };

    const contentCombined = (
      <>
        {content}
        {!useAriaLabelAttribute && propAriaLabel && (
          <span className="sr-only">&nbsp;{propAriaLabel}</span>
        )}
      </>
    );

    if (isExternal || isTel || isMailto) {
      return (
        <a {...basicProps} href={propTo} {...additionalProps}>
          {contentCombined}
        </a>
      );
    }

    return (
      <HashLink {...basicProps} smooth={true} to={propTo} {...additionalProps}>
        {contentCombined}
      </HashLink>
    );
  }
}

export default forwardRef((props, ref) => <Link {...props} forwardedRef={ref} />);
